import {
  Box,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Link as RouterLink,
  useNavigate,
} from 'react-router-dom';
import {
  ResourceGridActions,
  ResourceGridContext,
} from '../../hooks/useResourceGridActions';
import { ResourceGridActionMenu } from './ResourceGridActionMenu';

interface ResourceGridProps<T> {
  data: T[];
  columns: Column<T>[];
  stickyColumns?: boolean;
  // Two-pane layout for plans
  twoPaneMode?: boolean;
  leftPaneWidth?: string;
  rightPaneMinWidth?: string;
  onRowClick?: (row: T) => void;
  // Standardized action menu support
  actionMenuContext?: ResourceGridContext;
  actionMenuActions?: ResourceGridActions;
  planId?: string;
  marketId?: string;
}

export interface Column<T> {
  header: string; // Column header text (may be truncated)
  fullHeader?: string; // Full header text for tooltip
  /**
   * Let this column's header wrap over several lines instead of being clipped to
   * one, up to WRAPPED_HEADER_MAX_LINES, with the rest on hover past that.
   *
   * Opt-in per column rather than on by default: most headers here are one short
   * word and clipping never reaches them, whereas the insurance plan columns carry
   * full plan names that no sensible column width can hold on one line.
   */
  wrapHeader?: boolean;
  accessor: keyof T | string; // Key to access data
  isCustomRender?: boolean; // If custom rendering is needed
  render?: (data: T) => React.ReactNode; // Custom render function
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  isStatic?: boolean;
  linkAccessor?: string;
}

/**
 * How many lines a wrapping header may take before it is cut off.
 *
 * Four lines is about eighty characters at the 12rem these columns get, which
 * covers the plan names that are names: the longest real one measured, at 61
 * characters, still lands inside three lines. Past that the header row grows
 * taller than the rows it labels — a few plans carry whole notes inside the name
 * field, and unbounded wrapping put a 292-character one on 13 lines for a 249px
 * header row against 89px clamped.
 */
const WRAPPED_HEADER_MAX_LINES = 4;

const clampedTextStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: WRAPPED_HEADER_MAX_LINES,
  overflow: 'hidden',
};

/**
 * A header that wraps over several lines, cut off at WRAPPED_HEADER_MAX_LINES,
 * with the whole text on hover or focus.
 *
 * The tooltip is armed only when the text is really cut off, which takes a
 * measurement: a tooltip repeating text that is already fully on screen is noise,
 * and most of these headers do fit.
 */
const WrappedHeaderText = ({
  text,
  linkAccessor,
}: {
  text: string;
  linkAccessor?: string;
}) => {
  // A callback ref rather than useRef: the element sits in one of the two
  // branches below, so holding it in state is what re-measures once it mounts.
  const [textEl, setTextEl] = useState<HTMLElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);

  // Watch the element, not the window. The clamp turns on and off with the box's
  // own size, which the two-pane header sync and the breakpoint font change move
  // without a window resize — and a window listener per plan column is dozens of
  // forced reflows on every resize event.
  useEffect(() => {
    if (!textEl) return;

    const observer = new ResizeObserver(() =>
      setIsClamped(textEl.scrollHeight > textEl.clientHeight)
    );
    observer.observe(textEl);
    return () => observer.disconnect();
  }, [textEl, text]);

  return (
    <Tooltip
      label={text}
      hasArrow
      placement="top"
      maxW="20rem"
      isDisabled={!isClamped}
    >
      {linkAccessor ? (
        // A real anchor, not an <a> with onClick. It looks and behaves like a
        // link, so it has to be reachable by Tab, activate on Enter, and open in
        // a new tab on ctrl/middle click. The tab stop also carries the tooltip:
        // once the name is clamped, hover is the only way to read the tail, and
        // mouse-only is not a way to publish information.
        <Link
          as={RouterLink}
          to={`/plans/${linkAccessor}`}
          ref={setTextEl}
          color="blue.500"
          textDecoration="underline"
          _hover={{ textDecoration: 'none' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'blue.600',
            outlineOffset: '2px',
            borderRadius: '2px',
          }}
          style={clampedTextStyle}
        >
          {text}
        </Link>
      ) : (
        <Box ref={setTextEl} style={clampedTextStyle}>
          {text}
        </Box>
      )}
    </Tooltip>
  );
};

const ResourceGrid = <T,>({
  data,
  columns,
  stickyColumns = false,
  twoPaneMode = false,
  leftPaneWidth = '35%',
  rightPaneMinWidth = '560px',
  onRowClick,
  actionMenuContext,
  actionMenuActions,
  planId,
  marketId,
}: ResourceGridProps<T>) => {
  const staticColumns = columns.filter(
    (col) => col.isStatic === true
  );
  const dynamicColumns = columns.filter((col) => !col.isStatic);
  const navigate = useNavigate();

  // Helper function to render standardized action menu
  const renderActionMenu = useCallback(
    (row: T) => {
      if (!actionMenuContext || !actionMenuActions) {
        return null;
      }

      // Type guard to ensure we have an InsurancePlan
      const insuranceRow =
        row as unknown as import('../../types/plans/types').InsurancePlan;

      return (
        <ResourceGridActionMenu
          row={insuranceRow}
          context={actionMenuContext}
          actions={actionMenuActions}
          planId={planId}
          marketId={marketId}
        />
      );
    },
    [actionMenuContext, actionMenuActions, planId, marketId]
  );

  // Refs for synchronized scrolling
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const leftTableRef = useRef<HTMLTableElement>(null);
  const rightTableRef = useRef<HTMLTableElement>(null);

  // Row height synchronization
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const leftRowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const rightRowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  // Header height synchronization
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const leftHeaderRef = useRef<HTMLTableRowElement>(null);
  const rightHeaderRef = useRef<HTMLTableRowElement>(null);

  // Compute row heights for synchronization
  const computeRowHeights = useCallback(() => {
    if (data.length === 0) return;

    const heights: number[] = [];
    const minHeight = 44; // 2.75rem in pixels

    for (let i = 0; i < data.length; i++) {
      const leftHeight =
        leftRowRefs.current[i]?.getBoundingClientRect().height || 0;
      const rightHeight =
        rightRowRefs.current[i]?.getBoundingClientRect().height || 0;
      const maxHeight = Math.max(minHeight, leftHeight, rightHeight);
      heights.push(maxHeight);
    }

    setRowHeights(heights);
  }, [data.length]);

  // Compute header height for synchronization
  const computeHeaderHeight = useCallback(() => {
    const leftHeight =
      leftHeaderRef.current?.getBoundingClientRect().height || 0;
    const rightHeight =
      rightHeaderRef.current?.getBoundingClientRect().height || 0;
    const maxHeight = Math.max(leftHeight, rightHeight);
    setHeaderHeight(maxHeight);
  }, []);

  // Synchronize scrolling between panes
  const handleLeftScroll = useCallback(() => {
    if (rightPaneRef.current && leftPaneRef.current) {
      rightPaneRef.current.scrollTop = leftPaneRef.current.scrollTop;
    }
  }, []);

  const handleRightScroll = useCallback(() => {
    if (leftPaneRef.current && rightPaneRef.current) {
      leftPaneRef.current.scrollTop = rightPaneRef.current.scrollTop;
    }
  }, []);

  // Update heights when data changes
  useEffect(() => {
    const timer = setTimeout(() => {
      computeRowHeights();
      computeHeaderHeight();
    }, 100);

    return () => clearTimeout(timer);
  }, [data, computeRowHeights, computeHeaderHeight]);

  // Re-synchronize header heights when the right-pane header reflows (window
  // resize, column-width change) without waiting for a data update.
  useEffect(() => {
    const el = rightHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => computeHeaderHeight());
    observer.observe(el);
    return () => observer.disconnect();
  }, [computeHeaderHeight]);

  // Set up scroll synchronization
  useEffect(() => {
    const leftPane = leftPaneRef.current;
    const rightPane = rightPaneRef.current;

    if (leftPane) {
      leftPane.addEventListener('scroll', handleLeftScroll);
    }
    if (rightPane) {
      rightPane.addEventListener('scroll', handleRightScroll);
    }

    return () => {
      if (leftPane) {
        leftPane.removeEventListener('scroll', handleLeftScroll);
      }
      if (rightPane) {
        rightPane.removeEventListener('scroll', handleRightScroll);
      }
    };
  }, [handleLeftScroll, handleRightScroll]);
  // Render two-pane layout for plans
  if (twoPaneMode) {
    return (
      <TableContainer
        borderRadius="0.25rem"
        border="1px solid"
        borderColor="border.gray"
        overflow="hidden"
        height="100%"
        maxHeight="100%"
        position="relative"
      >
        <Box display="flex" height="100%" position="relative">
          {/* Left Pane - Provider Info (Fixed Width) */}
          <Box
            ref={leftPaneRef}
            width={leftPaneWidth}
            minWidth={{
              base: '300px',
              md: '350px',
              lg: '400px',
              xl: '450px',
            }}
            maxWidth={{
              base: '400px',
              md: '500px',
              lg: '600px',
              xl: '700px',
            }}
            height="100%"
            maxHeight="100%"
            borderRight="1px solid"
            borderRightColor="border.gray"
            overflowY="auto"
            overflowX="auto"
            bg="white"
            position="relative"
            zIndex="2"
            boxShadow="2px 0 4px rgba(0,0,0,0.1)"
          >
            <Table
              ref={leftTableRef}
              variant="unstyled"
              size={{ base: 'xs', md: 'sm' }}
              width="100%"
              minWidth="fit-content"
            >
              <Thead
                position="sticky"
                top="0"
                zIndex="4"
                bg="background.surfaceTeritiary"
                boxShadow="0 2px 4px rgba(0,0,0,0.1)"
              >
                <Tr
                  ref={leftHeaderRef}
                  height={
                    headerHeight > 0 ? `${headerHeight}px` : '2.75rem'
                  }
                >
                  {staticColumns.map((col, colIndex) => (
                    <Th
                      key={`left-${col.header}-${colIndex}`}
                      padding={{
                        base: '0.5rem 0.75rem',
                        md: '0.75rem 1rem',
                      }}
                      borderBottom="1px solid"
                      borderBottomColor="border.gray"
                      bg="background.surfaceTeritiary"
                      textAlign="left"
                      fontSize={{ base: '0.75rem', md: '0.875rem' }}
                      fontWeight="600"
                      color="gray.700"
                      width={col.width || 'auto'}
                      minWidth={col.minWidth || 'auto'}
                      whiteSpace="nowrap"
                    >
                      {col.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.map((row, rowIndex) => (
                  <Tr
                    key={rowIndex}
                    ref={(el) => {
                      leftRowRefs.current[rowIndex] = el;
                    }}
                    height={
                      rowHeights[rowIndex]
                        ? `${rowHeights[rowIndex]}px`
                        : '2.75rem'
                    }
                    onClick={() => onRowClick?.(row)}
                    cursor={onRowClick ? 'pointer' : 'default'}
                    _hover={{ bg: 'gray.50' }}
                    transition="background-color 0.2s ease"
                  >
                    {staticColumns.map((col, colIndex) => (
                      <Td
                        key={`left-${col.header}-${rowIndex}-${colIndex}`}
                        padding={{
                          base: '0.5rem 0.75rem',
                          md: '0.75rem 1rem',
                        }}
                        borderBottom="1px solid"
                        borderBottomColor="border.gray"
                        bg="transparent"
                        fontSize={{ base: '0.7rem', md: '0.75rem' }}
                        lineHeight={{ base: '0.9rem', md: '1rem' }}
                        width={col.width || 'auto'}
                        minWidth={col.minWidth || 'auto'}
                        whiteSpace="nowrap"
                        title={
                          col.isCustomRender && col.render
                            ? undefined
                            : typeof col.accessor === 'string'
                            ? String(
                                (row as Record<string, unknown>)[
                                  col.accessor
                                ] ?? '-'
                              )
                            : String(row[col.accessor] ?? '-')
                        }
                      >
                        {col.accessor === 'actions' &&
                        actionMenuContext &&
                        actionMenuActions
                          ? renderActionMenu(row)
                          : col.isCustomRender && col.render
                          ? col.render(row)
                          : typeof col.accessor === 'string'
                          ? String(
                              (row as Record<string, unknown>)[
                                col.accessor
                              ] ?? '-'
                            )
                          : String(row[col.accessor] ?? '-')}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Right Pane - Insurance Plans (Scrollable) */}
          <Box
            ref={rightPaneRef}
            flex="1"
            minWidth={{
              base: '400px',
              md: '500px',
              lg: rightPaneMinWidth,
            }}
            height="100%"
            maxHeight="100%"
            overflowY="auto"
            overflowX="auto"
            bg="white"
            position="relative"
          >
            <Table
              ref={rightTableRef}
              variant="unstyled"
              size={{ base: 'xs', md: 'sm' }}
              width="100%"
              minWidth="fit-content"
            >
              <Thead
                position="sticky"
                top="0"
                zIndex="4"
                bg="background.surfaceTeritiary"
                boxShadow="0 2px 4px rgba(0,0,0,0.1)"
              >
                <Tr
                  ref={rightHeaderRef}
                  height={
                    headerHeight > 0 ? `${headerHeight}px` : '2.75rem'
                  }
                >
                  {dynamicColumns.map((col, colIndex) => (
                    <Th
                      key={`right-${col.header}-${colIndex}`}
                      padding={{
                        base: '0.5rem 0.75rem',
                        md: '0.75rem 1rem',
                      }}
                      borderBottom="1px solid"
                      borderBottomColor="border.gray"
                      bg="background.surfaceTeritiary"
                      textAlign="left"
                      fontSize={{ base: '0.75rem', md: '0.875rem' }}
                      fontWeight="600"
                      color="gray.700"
                      width={
                        col.width || { base: '8rem', md: '10rem' }
                      }
                      minWidth={
                        col.minWidth || { base: '6rem', md: '8rem' }
                      }
                      whiteSpace={col.wrapHeader ? 'normal' : 'nowrap'}
                      overflow={col.wrapHeader ? 'visible' : 'hidden'}
                      textOverflow={col.wrapHeader ? 'clip' : 'ellipsis'}
                      wordBreak={col.wrapHeader ? 'break-word' : undefined}
                      verticalAlign={col.wrapHeader ? 'bottom' : undefined}
                    >
                      {col.wrapHeader ? (
                        <WrappedHeaderText
                          text={col.header}
                          linkAccessor={col.linkAccessor}
                        />
                      ) : col.fullHeader &&
                        col.fullHeader !== col.header ? (
                        <Tooltip
                          label={col.fullHeader}
                          hasArrow
                          placement="top"
                          maxW="20rem"
                        >
                          {col.linkAccessor ? (
                            <Link
                              color="blue.500"
                              textDecoration="underline"
                              _hover={{ textDecoration: 'none' }}
                              onClick={() =>
                                navigate(`/plans/${col.linkAccessor}`)
                              }
                              display="block"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {col.header}
                            </Link>
                          ) : (
                            <Box
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              cursor="help"
                            >
                              {col.header}
                            </Box>
                          )}
                        </Tooltip>
                      ) : col.linkAccessor ? (
                        <Link
                          color="blue.500"
                          textDecoration="underline"
                          _hover={{ textDecoration: 'none' }}
                          onClick={() =>
                            navigate(`/plans/${col.linkAccessor}`)
                          }
                        >
                          {col.header}
                        </Link>
                      ) : (
                        col.header
                      )}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.map((row, rowIndex) => (
                  <Tr
                    key={rowIndex}
                    ref={(el) => {
                      rightRowRefs.current[rowIndex] = el;
                    }}
                    height={
                      rowHeights[rowIndex]
                        ? `${rowHeights[rowIndex]}px`
                        : '2.75rem'
                    }
                    onClick={() => onRowClick?.(row)}
                    cursor={onRowClick ? 'pointer' : 'default'}
                    _hover={{ bg: 'gray.50' }}
                    transition="background-color 0.2s ease"
                  >
                    {dynamicColumns.map((col, colIndex) => (
                      <Td
                        key={`right-${col.header}-${rowIndex}-${colIndex}`}
                        padding={{
                          base: '0.5rem 0.75rem',
                          md: '0.75rem 1rem',
                        }}
                        borderBottom="1px solid"
                        borderBottomColor="border.gray"
                        bg="transparent"
                        fontSize={{ base: '0.7rem', md: '0.75rem' }}
                        lineHeight={{ base: '0.9rem', md: '1rem' }}
                        width={
                          col.width || { base: '8rem', md: '10rem' }
                        }
                        minWidth={
                          col.minWidth || { base: '6rem', md: '8rem' }
                        }
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        title={
                          col.isCustomRender && col.render
                            ? undefined
                            : typeof col.accessor === 'string'
                            ? String(
                                (row as Record<string, unknown>)[
                                  col.accessor
                                ] ?? '-'
                              )
                            : String(row[col.accessor] ?? '-')
                        }
                      >
                        {col.accessor === 'actions' &&
                        actionMenuContext &&
                        actionMenuActions
                          ? renderActionMenu(row)
                          : col.isCustomRender && col.render
                          ? col.render(row)
                          : typeof col.accessor === 'string'
                          ? String(
                              (row as Record<string, unknown>)[
                                col.accessor
                              ] ?? '-'
                            )
                          : String(row[col.accessor] ?? '-')}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </TableContainer>
    );
  }

  // Render traditional single-table layout for other pages
  return (
    <TableContainer
      borderRadius="0.25rem"
      border="1px solid"
      borderColor="border.gray"
      overflow="auto"
    >
      <Box display="flex">
        {/* Static Columns Container */}
        {stickyColumns && staticColumns.length > 0 && (
          <Box
            position="sticky"
            left="0"
            zIndex="2"
            bg="white"
            borderRight="0.25rem solid"
            borderColor="border.gray"
          >
            <Table
              variant="unstyled"
              size="sm"
              width="auto"
              borderRadius="0.25rem"
            >
              <Thead>
                <Tr height="2.75rem">
                  {staticColumns.map((col, colIndex) => (
                    <Th
                      key={`static-${col.header}-${colIndex}`}
                      padding="0.75rem 1rem"
                      borderBottom="1px solid"
                      borderBottomColor="border.gray"
                      bg="background.surfaceTeritiary"
                      textAlign="left"
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.700"
                      width={col.width || 'auto'}
                      position="sticky"
                      left="0"
                      zIndex="2"
                      bgColor="white"
                    >
                      {col.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.map((row, rowIndex) => (
                  <Tr
                    key={rowIndex}
                    height="2.75rem"
                    onClick={() => onRowClick?.(row)}
                    cursor={onRowClick ? 'pointer' : 'default'}
                    _hover={onRowClick ? { bg: 'gray.50' } : {}}
                  >
                    {staticColumns.map((col, colIndex) => (
                      <Td
                        key={`static-${col.header}-${rowIndex}-${colIndex}`}
                        padding="0.75rem 1rem"
                        borderBottom="1px solid"
                        borderBottomColor="border.gray"
                        bg="background.primary"
                        fontSize="0.75rem"
                        lineHeight="1rem"
                        width={col.width || 'auto'}
                        position="sticky"
                        left="0"
                        zIndex="1"
                        bgColor="white"
                      >
                        {col.isCustomRender && col.render ? (
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            alignItems="center"
                          >
                            {col.render(row)}
                          </Box>
                        ) : typeof col.accessor === 'string' ? (
                          String(
                            (row as Record<string, unknown>)[
                              col.accessor
                            ] ?? '-'
                          )
                        ) : (
                          String(row[col.accessor] ?? '-')
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Dynamic Columns (Scrollable) */}
        <Box overflowX="auto" flex="1">
          <Table
            variant="unstyled"
            size="sm"
            width="100%"
            borderRadius="0.25rem"
          >
            <Thead>
              <Tr height="2.75rem">
                {dynamicColumns.map((col) => (
                  <Th
                    key={col.header}
                    padding="0.75rem 1rem"
                    borderBottom="1px solid"
                    borderBottomColor="border.gray"
                    bg="background.surfaceTeritiary"
                    textAlign="left"
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.700"
                    width={col.width}
                    minWidth={col.minWidth}
                    maxWidth={col.maxWidth}
                    style={
                      col.wrapHeader
                        ? {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            verticalAlign: 'bottom',
                          }
                        : {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                    }
                  >
                    {col.wrapHeader ? (
                      <WrappedHeaderText
                        text={col.header}
                        linkAccessor={col.linkAccessor}
                      />
                    ) : col.fullHeader &&
                      col.fullHeader !== col.header ? (
                      <Tooltip
                        label={col.fullHeader}
                        hasArrow
                        placement="top"
                        maxW="20rem"
                      >
                        {col.linkAccessor ? (
                          <Link
                            color="blue.500"
                            textDecoration="underline"
                            _hover={{ textDecoration: 'none' }}
                            onClick={() =>
                              navigate(`/plans/${col.linkAccessor}`)
                            }
                            display="block"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {col.header}
                          </Link>
                        ) : (
                          <Box
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            cursor="help"
                          >
                            {col.header}
                          </Box>
                        )}
                      </Tooltip>
                    ) : col.linkAccessor ? (
                      <Link
                        color="blue.500"
                        textDecoration="underline"
                        _hover={{ textDecoration: 'none' }}
                        onClick={() =>
                          navigate(`/plans/${col.linkAccessor}`)
                        }
                      >
                        {col.header}
                      </Link>
                    ) : (
                      col.header
                    )}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {data.map((row, rowIndex) => (
                <Tr
                  key={rowIndex}
                  height="2.75rem"
                  onClick={() => onRowClick?.(row)}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  _hover={onRowClick ? { bg: 'gray.50' } : {}}
                >
                  {dynamicColumns.map((col, colIndex) => (
                    <Td
                      key={`dynamic-${col.header}-${rowIndex}-${colIndex}`}
                      padding="0.75rem 1rem"
                      borderBottom="1px solid"
                      borderBottomColor="border.gray"
                      bg="background.primary"
                      fontSize="0.75rem"
                      lineHeight="1rem"
                      width={col.width}
                    >
                      {col.isCustomRender && col.render ? (
                        <Box
                          display="flex"
                          justifyContent="flex-start"
                          alignItems="center"
                        >
                          {col.render(row)}
                        </Box>
                      ) : typeof col.accessor === 'string' ? (
                        String(
                          (row as Record<string, unknown>)[
                            col.accessor
                          ] ?? '-'
                        )
                      ) : (
                        String(row[col.accessor] ?? '-')
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </TableContainer>
  );
};

export default ResourceGrid;
